
import Vue from 'vue';
import moment from 'moment-timezone';
import { functions } from '@/firebase';
import _trim from 'lodash/trim';
import { mapActions, mapGetters, mapState } from 'vuex';
import EnterpriseUpsellWrapper from '@/components/EnterpriseUpsellWrapper/EnterpriseUpsellWrapper.vue';
import { analytics, EVENTS } from '@/services/analytics';
import DialogContent from '@/components/base/DialogContent.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import { EMAIL_REGEX, SNACKBAR_STATUS } from '@/constants/constants';
import ActionsIsland from '@/components/ActionsIsland.vue';
import { db } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins/VuexFirestoreBindingsMixin';
import { TeamUserRoleDef } from '@run-diffusion/shared';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';
import RoleAutoAssignExplainer from '@/components/RoleAutoAssignExplainer/RoleAutoAssignExplainer.vue';
import RoleDefBadge from '@/views/Team/CompanyDashboard/RoleDefBadge.vue';
import { scimProvisioningMembershipTooltip } from '@/constants/scimProvisioning';
import { JUST_ADDED_LIST, noteJustAdded } from '@/utils/justAdded';
import { justAddedTeamMemberRow } from '@/views/Team/teamTablesState';

function buildInviteTeamMembersFailureMessage (result: {
	message?: string;
	failedCount?: number;
	results?: Array<{ status?: string; email?: string; reason?: string }>;
}): string {
	const failed = (result.results || []).filter((r) => r.status === 'failed');
	if (failed.length === 1) {
		const f = failed[0];
		if (f.reason) return `${f.email}: ${f.reason}`;
		if (f.email) return `Could not invite ${f.email}.`;
	}
	if (failed.length > 1) {
		const parts = failed.map((f) => (f.reason ? `${f.email}: ${f.reason}` : f.email));
		const joined = parts.join(' ');
		const maxLen = 400;
		if (joined.length <= maxLen) {
			return `${failed.length} invites could not be sent — ${joined}`;
		}
		return `${failed.length} invites could not be sent. First: ${parts[0]}`;
	}
	return result.message || 'One or more invites could not be sent.';
}

export default Vue.extend({
	name: 'InviteTeamMember',
	mixins: [
		TeamUserMixin,
	],
	props: {
		currTeam: { type: Object, default: null },
		currTeamUserRole: { type: Object, default: null },
		disabledByScimProvisioning: { type: Boolean, default: false },
	},
	data () {
		const MAX_NUM_EMAILS: number = 100;
		const splitEmails = (str: string): string[] => {
			if (!str) return [];
			// Split by comma, semicolon, or any whitespace grouping (spaces, tabs, newlines, etc.)
			return str.split(/[,;]|\s+/).map(email => _trim(email)).filter(email => email);
		};
		const hasCeilingNumberOfCommas = (str: string, ceiling: number): boolean => {
			if (!str) return false;
			// Count the number of emails after splitting
			const emails: string[] = splitEmails(str);
			return emails.length <= ceiling;
		};
		const isListOfEmails = (str) => {
			if (!str) return false;
			// Split the string by commas, semicolons, and newlines
			const emails: string[] = splitEmails(str);

			// Loop through each email in the list and check if it's valid
			for (let i = 0; i < emails.length; i++) {
				const email: string = emails[i];

				// Use a regular expression to check if the email is valid
				if (!EMAIL_REGEX.test(email)) {
					return false; // If any email is invalid, return false
				}
			}

			// If we made it through the loop without finding any invalid emails, return true
			return true;
		};

		return {
			MAX_NUM_EMAILS: MAX_NUM_EMAILS,
			dialogOpen: false,
			inviteDisabled: false,
			inviteLoading: false,

			inviteEmails: null,

			// New Roles (TeamUserRoleDefs)
			availableRoleDefs: [] as TeamUserRoleDef[],
			loadingAvailableRoleDefs: false,
			selectedRoleDefId: null as string | null,

			// FORM
			formValid: true,
			splitEmails,
			rules: {
				required: [
					v => !!v || 'Required field',
				],
				commaSeparatedList: [
					v => isListOfEmails(v) || 'Must be a list of emails separated by commas, semicolons, spaces, or returns',
				],
				maxNumberOfEmails: [
					v => hasCeilingNumberOfCommas(v, MAX_NUM_EMAILS) || `Can only put in ${MAX_NUM_EMAILS} emails at a time`,
				],
			},
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		scimMembershipTooltip (): string {
			return scimProvisioningMembershipTooltip(this.displayLabels);
		},
		emailsPlaceholder (): string {
			const userDomain: string = this.user?.email?.split('@')?.[1] || 'gmail.com';
			return `abc@${userDomain}, 123@${userDomain}; xyz@${userDomain}`;
		},
		roleDefItems () {
			return [
				{
					value: null,
					text: '✨ Automatically assign',
					// No roleDef: this entry is the absence of a choice, not a role, so it stays plain text.
					roleDef: null,
				},
				...this.availableRoleDefs.map(role => ({
					value: role.id,
					text: role.name,
					roleDef: role,
				})),
			];
		},
		lowestRoleDef (): TeamUserRoleDef | null {
			if (!(this.availableRoleDefs || []).length) return null;
			return this.availableRoleDefs[this.availableRoleDefs.length - 1] as TeamUserRoleDef;
		},
		lowestRoleLabel (): string {
			return (this.lowestRoleDef && this.lowestRoleDef.name) ? this.lowestRoleDef.name : '';
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'setUpsellDialog',
		]),
		onTeamRolesUpsell () {
			this.setUpsellDialog({ noTeamRoles: true });
		},
		async bindTeamUserRoleDefs () {
			this.loadingAvailableRoleDefs = true;
			try {
				const companyId: string = this.currTeam?.companyId;
				if (!companyId) return;

				// index created: teamUserRoleDefs: companyId Ascending isDeleted Ascending level Descending __name__ Descending
				const roleDefsRef = db
					.collection('teamUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('level', 'desc');

				await this.$bind(
					'availableRoleDefs',
					roleDefsRef,
					get$bindFirestoreOptions(),
				);
			} catch (e) {
				console.error('Error binding team user role defs:', e);
			} finally {
				this.loadingAvailableRoleDefs = false;
			}
		},
		onResetAndClose () {
			this.dialogOpen = false;
			this.inviteEmails = null;
			this.selectedRoleDefId = null;
		},
		async onInviteClick () {
			if (!this.currTeam) return;

			// If company is not enterprise, then don't allow manual team role assignment
			if (!this.currTeam.company?.clubOffer?.benefits?.isEnterprise) {
				this.selectedRoleDefId = null;
			}

			this.inviteLoading = true;
			const onError: Function = (e) => {
				console.error('Error sending team member invites: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error sending email invites, please try again',
					show: true,
				});
			}

			try {
				const invites: { email: string; teamUserRoleDefId: string; }[] = [];
				const teamMemberInviteEmails: string[] = this.splitEmails(this.inviteEmails)
					.map((email: string) => email.toLowerCase());

				teamMemberInviteEmails.forEach((email: string) => {
					const invite: any = {
						email: email,
					};
					invite.teamUserRoleDefId = this.selectedRoleDefId;

					invites.push(invite);
				});

				const functionRef = functions
					.httpsCallable('inviteTeamMembers');
				const result = (await functionRef({
					teamId: this.currTeam.id,
					invites,
				})).data as {
					success?: boolean;
					message?: string;
					invitedCount?: number;
					skippedCount?: number;
					failedCount?: number;
					results?: Array<{ status?: string; email?: string; reason?: string }>;
				};

				const invitedCount = result.invitedCount ?? 0;
				const skippedCount = result.skippedCount ?? 0;
				const failedCount = result.failedCount ?? 0;
				if (result.success) {
					analytics.track(EVENTS.TEAM_MEMBER_INVITED, {
						team_id: this._get(this.currTeam, 'id', null),
						invited_count: invitedCount,
						skipped_count: skippedCount,
						failed_count: failedCount,
						role_def_id: this.selectedRoleDefId || null,
					});
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Invites processed: ${invitedCount} sent${skippedCount > 0 ? `, ${skippedCount} skipped` : ''}.`,
						show: true,
					});
					this.noteInvitedJustAdded(result.results || []);
					this.$emit('on-success');
				} else {
					console.error('inviteTeamMembers failed:', result);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: buildInviteTeamMembersFailureMessage(result),
						show: true,
					});
				}
			} catch (e) {
				onError(e);
			} finally {
				this.inviteLoading = false;
				this.onResetAndClose();
			}
		},
		/**
		 * The Team's members list has these only once the mirror does, so each
		 * address invited is pinned on top of it until then (utils/justAdded.ts).
		 * The membership's id is the address.
		 */
		noteInvitedJustAdded (results: Array<{ status?: string; email?: string }>) {
			const roleDef: TeamUserRoleDef | undefined = (this.availableRoleDefs || [])
				.find((role: TeamUserRoleDef) => role.id === this.selectedRoleDefId);
			const invitedIso: string = moment().toISOString();
			noteJustAdded(JUST_ADDED_LIST.teamMembers(this.currTeam.id), results
				.filter((invite) => invite.status === 'invited' && !!invite.email)
				.map((invite) => ({
					id: invite.email.trim().toLowerCase(),
					row: justAddedTeamMemberRow({
						email: invite.email,
						invitedIso,
						teamRole: roleDef ? { id: roleDef.id, name: roleDef.name || null } : null,
					}),
				})));
		},
	},
	watch: {
		dialogOpen (val: boolean) {
			if (val) this.bindTeamUserRoleDefs();
		},
	},
	components: {
		DialogContent,
		DialogOrBottomSheet,
		ActionsIsland,
		EnterpriseUpsellWrapper,
		RoleAutoAssignExplainer,
		RoleDefBadge,
	},
});
